import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ProjectCreator } from "@t3tools/contracts";

import type { Project } from "../types";
import { readEnvironmentApi } from "../environmentApi";
import { useServerAuthPolicy } from "../environments/primary/auth";
import { currentServerToken, serverTokenSubject } from "../environments/primary/serverToken";
import { newCommandId } from "../lib/utils";
import { refreshArchivedThreadsForEnvironment } from "../lib/archivedThreadsState";
import { useComposerDraftStore } from "../composerDraftStore";
import { scopeProjectRef } from "@t3tools/client-runtime";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";

export function canManageProject(
  teamServer: boolean,
  owner: ProjectCreator | null | undefined,
  subject: string | null,
) {
  return !teamServer || owner == null || owner.userId === subject;
}

export function useProjectManagementAccess(owner: ProjectCreator | null | undefined) {
  const teamServer = useServerAuthPolicy((state) => state.policy === "team-server");
  const token = currentServerToken();
  return {
    teamServer,
    allowed: canManageProject(teamServer, owner, token === null ? null : serverTokenSubject(token)),
  };
}

export async function changeProject(
  project: Project,
  action: "archive" | "unarchive" | "delete",
  deleteFiles = false,
) {
  const api = readEnvironmentApi(project.environmentId);
  if (!api) throw new Error("Project API unavailable.");
  await api.orchestration.dispatchCommand({
    type:
      action === "delete"
        ? "project.delete"
        : action === "archive"
          ? "project.archive"
          : "project.unarchive",
    commandId: newCommandId(),
    projectId: project.id,
    ...(action === "delete" ? { force: true, deleteFiles } : {}),
  });
  if (action !== "unarchive") {
    const ref = scopeProjectRef(project.environmentId, project.id);
    const drafts = useComposerDraftStore.getState();
    const draft = drafts.getDraftThreadByProjectRef(ref);
    if (draft) drafts.clearDraftThread(draft.draftId);
    drafts.clearProjectDraftThreadId(ref);
  }
  refreshArchivedThreadsForEnvironment(project.environmentId);
}

export function ProjectActionsDialog({
  project,
  archived = false,
  onClose,
  onChanged,
}: {
  project: Project;
  archived?: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const { teamServer, allowed } = useProjectManagementAccess(project.createdBy);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (action: "archive" | "delete") => {
    if (!allowed || busy) return;
    setBusy(true);
    try {
      await changeProject(project, action, action === "delete" && teamServer);
      onChanged?.();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("common/anErrorOccurred_gd0s0s"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogPopup showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{project.name}</DialogTitle>
          <DialogDescription>
            {!archived && (
              <span className="mb-3 block">{t("common/archivingMovesTheProject_zbwlq5")}</span>
            )}
            {teamServer ? t("common/ifYouDeleteThe_vftiyp") : t("common/theProjectAndIts_1wj8f7")}
          </DialogDescription>
        </DialogHeader>
        {!allowed && (
          <p role="status" className="px-6 pb-4">
            {t("common/onlyTheProjectOwner_rfeqph", {
              name: project.createdBy?.name ?? project.createdBy?.userId,
            })}
          </p>
        )}
        {error && (
          <p role="alert" className="px-6 pb-4 text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" className="sm:me-auto" disabled={busy} onClick={onClose}>
            {t("common/cancel_8mt5fw")}
          </Button>
          {!archived && (
            <Button disabled={!allowed || busy} onClick={() => void run("archive")}>
              {t("common/archive_4zqr4y")}
            </Button>
          )}
          <Button
            variant="destructive"
            disabled={!allowed || busy}
            onClick={() => void run("delete")}
          >
            {teamServer
              ? t("common/deleteFilesAndChats_1rjh3h")
              : t("common/removeFromDovod_e9pspn")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
